import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Film, ChevronDown } from 'lucide-react';
import { FilterBar } from './FilterBar';
import { MovieGrid } from './MovieGrid';
import { MediaToggle } from './MediaToggle';
import { useMovies } from '../hooks/useMovies';
import { useMediaType } from '../contexts/MediaTypeContext';
import { SORT_OPTIONS, CURRENT_YEAR } from '../utils/constants';
import type { MediaItem, SortOption, Filters, MediaType } from '../types/movie';

// Parse filters from URL search params
function parseFiltersFromURL(searchParams: URLSearchParams): Partial<Filters> {
  const filters: Partial<Filters> = {};

  const genres = searchParams.get('genres');
  if (genres) filters.genres = genres.split(',').map(Number).filter(Boolean);

  const yearMin = searchParams.get('yearMin');
  const yearMax = searchParams.get('yearMax');
  if (yearMin || yearMax) {
    filters.yearRange = [
      yearMin ? parseInt(yearMin) : 1920,
      yearMax ? parseInt(yearMax) : CURRENT_YEAR,
    ];
  }

  const runtimeMin = searchParams.get('runtimeMin');
  const runtimeMax = searchParams.get('runtimeMax');
  if (runtimeMin || runtimeMax) {
    filters.runtimeRange = [
      runtimeMin ? parseInt(runtimeMin) : 0,
      runtimeMax ? parseInt(runtimeMax) : 300,
    ];
  }

  const ratingMin = searchParams.get('ratingMin');
  const ratingMax = searchParams.get('ratingMax');
  if (ratingMin || ratingMax) {
    filters.ratingRange = [
      ratingMin ? parseFloat(ratingMin) : 0,
      ratingMax ? parseFloat(ratingMax) : 10,
    ];
  }

  const rtMin = searchParams.get('rtMin');
  const rtMax = searchParams.get('rtMax');
  if (rtMin || rtMax) {
    filters.rtRatingRange = [
      rtMin ? parseInt(rtMin) : 0,
      rtMax ? parseInt(rtMax) : 100,
    ];
  }

  const ourMin = searchParams.get('ourMin');
  const ourMax = searchParams.get('ourMax');
  if (ourMin || ourMax) {
    filters.ourRatingRange = [
      ourMin ? parseFloat(ourMin) : 0,
      ourMax ? parseFloat(ourMax) : 10,
    ];
  }

  const languages = searchParams.get('languages');
  if (languages) filters.languages = languages.split(',');

  const sortBy = searchParams.get('sortBy');
  if (sortBy) filters.sortBy = sortBy as SortOption;

  const actor = searchParams.get('actor');
  if (actor) filters.actorSearch = actor;

  const director = searchParams.get('director');
  if (director) filters.directorSearch = director;

  return filters;
}

// Parse media type from URL
function parseMediaTypeFromURL(searchParams: URLSearchParams): MediaType | null {
  const type = searchParams.get('type');
  if (type === 'tv' || type === 'movie') return type;
  return null;
}

// Convert filters to URL search params
function filtersToURLParams(filters: Filters, mediaType: MediaType, searchQuery: string): URLSearchParams {
  const params = new URLSearchParams();

  // Always save media type if it's TV
  if (mediaType === 'tv') {
    params.set('type', 'tv');
  }

  if (filters.genres.length > 0) {
    params.set('genres', filters.genres.join(','));
  }

  if (filters.yearRange[0] > 1920 || filters.yearRange[1] < CURRENT_YEAR) {
    if (filters.yearRange[0] > 1920) params.set('yearMin', String(filters.yearRange[0]));
    if (filters.yearRange[1] < CURRENT_YEAR) params.set('yearMax', String(filters.yearRange[1]));
  }

  if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
    if (filters.runtimeRange[0] > 0) params.set('runtimeMin', String(filters.runtimeRange[0]));
    if (filters.runtimeRange[1] < 300) params.set('runtimeMax', String(filters.runtimeRange[1]));
  }

  if (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) {
    if (filters.ratingRange[0] > 0) params.set('ratingMin', String(filters.ratingRange[0]));
    if (filters.ratingRange[1] < 10) params.set('ratingMax', String(filters.ratingRange[1]));
  }

  if (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) {
    if (filters.rtRatingRange[0] > 0) params.set('rtMin', String(filters.rtRatingRange[0]));
    if (filters.rtRatingRange[1] < 100) params.set('rtMax', String(filters.rtRatingRange[1]));
  }

  if (filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10) {
    if (filters.ourRatingRange[0] > 0) params.set('ourMin', String(filters.ourRatingRange[0]));
    if (filters.ourRatingRange[1] < 10) params.set('ourMax', String(filters.ourRatingRange[1]));
  }

  if (filters.languages.length > 0) {
    params.set('languages', filters.languages.join(','));
  }

  if (filters.sortBy !== 'popularity.desc') {
    params.set('sortBy', filters.sortBy);
  }

  if (filters.actorSearch) {
    params.set('actor', filters.actorSearch);
  }

  if (filters.directorSearch) {
    params.set('director', filters.directorSearch);
  }

  if (searchQuery) {
    params.set('q', searchQuery);
  }

  return params;
}

export function FilmFinder() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const { mediaType, setMediaType } = useMediaType();

  // Parse initial filters and media type from URL (memoized to avoid re-parsing)
  const initialFilters = useRef<Partial<Filters>>(parseFiltersFromURL(searchParams));
  const initialUrlMediaType = useRef<MediaType | null>(parseMediaTypeFromURL(searchParams));
  const hasInitializedMediaType = useRef(false);

  // Set media type from URL on mount
  useEffect(() => {
    if (!hasInitializedMediaType.current) {
      hasInitializedMediaType.current = true;
      if (initialUrlMediaType.current) {
        setMediaType(initialUrlMediaType.current);
      }
    }
  }, [setMediaType]);

  // For the very first render only, use URL media type to avoid flash of wrong content
  // After that, always use context mediaType
  const effectiveMediaType = !hasInitializedMediaType.current && initialUrlMediaType.current
    ? initialUrlMediaType.current
    : mediaType;
  const isTV = effectiveMediaType === 'tv';

  const {
    movies,
    loading,
    error,
    hasMore,
    totalResults,
    loadMore,
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
  } = useMovies(initialFilters.current, effectiveMediaType);

  // Sync filters and media type to URL when they change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const newParams = filtersToURLParams(filters, effectiveMediaType, searchQuery);

    // Use replace to avoid creating new history entries on every filter change
    setSearchParams(newParams, { replace: true });
  }, [filters, searchQuery, effectiveMediaType, setSearchParams]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMovieClick = (item: MediaItem) => {
    const route = item.media_type === 'tv' ? `/tv/${item.id}` : `/movie/${item.id}`;
    navigate(route);
  };

  const handleSortChange = (sortBy: SortOption) => {
    setFilters({ ...filters, sortBy });
    setSortOpen(false);
  };

  const activeSortLabel = SORT_OPTIONS.find(o => o.value === filters.sortBy)?.label || 'Most Popular';

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-screen-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo + Media Toggle */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-400 via-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <Film size={24} className="text-white" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-2xl font-bold text-white">Filmple</h1>
                  <p className="text-xs text-slate-400">Find your next favorite {isTV ? 'show' : 'movie'}</p>
                </div>
              </div>
              {/* Media Type Toggle */}
              <MediaToggle />
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isTV ? "Search TV shows..." : "Search movies..."}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-700 border border-slate-600 rounded-full text-slate-200 placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Filter Bar (includes Active Filters) */}
      <FilterBar filters={filters} onChange={setFilters} mediaType={effectiveMediaType} />

      {/* Results info with Sort dropdown */}
      <div className="max-w-screen-2xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <p className="text-slate-400 text-sm">
            {loading ? 'Loading...' : `${totalResults.toLocaleString()} ${isTV ? 'TV shows' : 'movies'} found`}
          </p>
          <div className="flex items-center gap-4">
            {error && (
              <p className="text-amber-400 text-sm">
                Showing sample data
              </p>
            )}
            {/* Sort Dropdown */}
            <div ref={sortRef} className="relative">
              <button
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white hover:border-slate-600 transition-all"
              >
                <span className="text-sm">Sort: <span className="text-cyan-400 font-medium">{activeSortLabel}</span></span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="max-h-80 overflow-y-auto">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                          filters.sortBy === option.value
                            ? 'bg-cyan-500/20 text-cyan-400'
                            : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Movie Grid */}
      <main className="max-w-screen-2xl mx-auto px-4 pb-8">
        <MovieGrid
          movies={movies}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onMovieClick={handleMovieClick}
          mediaType={effectiveMediaType}
        />
      </main>
    </div>
  );
}
